import React, { useMemo } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { useSelector } from 'react-redux';
import { getDynamicBorder } from 'utils/appDesigner';
import './CustomPopup.scss';

interface Props {
    open: boolean;
    scrollType?: 'body' | 'paper';
    popupClassName?: string;
    popupContainerClassName?: string;
    dialogClassName?: string;
    isMultiLevelPopup?: boolean;
    isFullWidth?: boolean;
    zIndex?: number;
    isApplyEventThemed?: boolean;
    /** Opt-in event theming from Redux (e.g. CMS Directory preview). */
    useEventTheme?: boolean;
    /** With useEventTheme: show dynamic border on the dialog paper. */
    showEventThemeBorder?: boolean;
    paperMinWidth?: number;
    paperWidth?: number;
    onClose?: () => void;
}

const CustomPopup: React.FC<Props> = (props) => {
    const {
        open,
        children,
        scrollType = 'body',
        dialogClassName,
        popupClassName,
        popupContainerClassName = '',
        isMultiLevelPopup = false,
        isFullWidth = false,
        zIndex,
        isApplyEventThemed = true,
        useEventTheme = false,
        showEventThemeBorder = false,
        paperMinWidth,
        paperWidth,
        onClose,
    } = props;

    const themeColors = useSelector(
        (state: any) => state.eventWrapper?.themeColors
    );
    const isEventThemed =
        (useEventTheme && !!themeColors?.backgroundColor) ||
        (isApplyEventThemed &&
            document.body.getAttribute('data-event-themed') === 'true');

    const paperStyle = useMemo(() => {
        const style: React.CSSProperties = {};
        if (typeof paperMinWidth === 'number') {
            style.minWidth = paperMinWidth;
        }
        if (typeof paperWidth === 'number') {
            style.width = paperWidth;
            style.maxWidth = 'calc(100vw - 32px)';
        }
        if (
            useEventTheme &&
            showEventThemeBorder &&
            themeColors?.backgroundColor
        ) {
            const borderColor = getDynamicBorder({
                backgroundColor: themeColors.backgroundColor,
                adjustAmount: null,
                skipBorderValidation: true,
                returnColorOnly: true,
            });
            if (borderColor) {
                style.border = `1px solid ${borderColor}`;
                style.borderRadius = 8;
                style.overflow = 'hidden';
                style.backgroundColor = themeColors.backgroundColor;
            }
        }
        return Object.keys(style).length ? style : undefined;
    }, [
        paperMinWidth,
        paperWidth,
        useEventTheme,
        showEventThemeBorder,
        themeColors?.backgroundColor,
    ]);

    React.useEffect(() => {
        if (!isMultiLevelPopup) {
            if (open) {
                document.body.style.overflowY = 'hidden';
            } else {
                document.body.style.overflowY = 'visible';
            }
        }
    }, [open, isMultiLevelPopup]);

    return (
        <Dialog
            className={`popup ${isFullWidth ? 'full-width-popup' : ''} ${
                dialogClassName || ''
            } ${isEventThemed ? 'event-themed-popup' : ''}`}
            open={open}
            scroll={scrollType}
            onClose={(_event, reason) => {
                if (
                    onClose &&
                    (reason === 'backdropClick' || reason === 'escapeKeyDown')
                ) {
                    onClose();
                }
            }}
            onFocus={() => {
                document.body.style.overflowY = 'hidden';
            }}
            onClick={(e) => e.stopPropagation()}
            maxWidth="lg"
            disableScrollLock
            disableEnforceFocus
            style={zIndex ? { zIndex } : undefined}
            PaperProps={paperStyle ? { style: paperStyle } : undefined}
        >
            <div className={`max-size-popup ${popupContainerClassName ?? ''}`}>
                <div className={`popup-card ${popupClassName ?? ''}`}>
                    <>{children}</>
                </div>
            </div>
        </Dialog>
    );
};

export default CustomPopup;
